function isValidDate(value) {
  return value instanceof Date && Number.isFinite(value.getTime());
}

export function parseBookingStart(booking) {
  const dateValue = String(booking?.date || '').trim();
  const timeValue = String(booking?.time || '').trim() || '00:00';

  if (!dateValue) return null;

  const parsed = new Date(`${dateValue}T${timeValue}`);
  return isValidDate(parsed) ? parsed : null;
}

export function parseBookingEnd(booking) {
  const start = parseBookingStart(booking);
  if (!start) return null;

  const endDateValue = String(booking?.end_date || '').trim();
  const endTimeValue = String(booking?.end_time || '').trim();

  if (endDateValue) {
    const fallbackClock = String(booking?.time || '').trim() || '23:59';
    const parsedExplicitEnd = new Date(`${endDateValue}T${endTimeValue || fallbackClock}`);

    if (isValidDate(parsedExplicitEnd) && parsedExplicitEnd > start) {
      return parsedExplicitEnd;
    }
  }

  const durationMinutes = Number(booking?.duration);
  if (Number.isFinite(durationMinutes) && durationMinutes > 0) {
    return new Date(start.getTime() + durationMinutes * 60000);
  }

  return new Date(start.getTime() + 60 * 60000);
}

const RESOLVED_COMPLETED_STATUSES = new Set([
  'completed',
  'complete',
  'closed',
]);

const RESOLVED_DID_NOT_FLY_STATUSES = new Set([
  'did_not_fly',
  'not_flown',
]);

function normalizePostFlightStatus(status) {
  return String(status || '')
    .trim()
    .toLowerCase();
}

export function getResolvedPostFlightStatus(booking) {
  const normalized = normalizePostFlightStatus(
    booking?.tech_log_status
  );

  if (RESOLVED_DID_NOT_FLY_STATUSES.has(normalized)) {
    return 'did_not_fly';
  }

  if (RESOLVED_COMPLETED_STATUSES.has(normalized)) {
    return 'completed';
  }

  return null;
}

export function isPostFlightCompleted(booking) {
  return Boolean(getResolvedPostFlightStatus(booking));
}

const STATUSES_THAT_LOCK_DELETION = new Set([
  'completed',
  'complete',
  'closed',
  'attention_required',
  'defect_reported',
]);

export function isBookingStatusLockedForDeletion(booking) {
  const normalized = normalizePostFlightStatus(booking?.tech_log_status);
  return STATUSES_THAT_LOCK_DELETION.has(normalized);
}

export function isLogbookCompleted(booking) {
  // pilot_logbook_status is canonical; logbook_status is a legacy fallback
  // used only when the canonical field is genuinely absent or empty.
  const canonicalStatus = String(booking?.pilot_logbook_status || '').trim().toLowerCase();
  const status = canonicalStatus || String(booking?.logbook_status || '').trim().toLowerCase();

  if (status === 'complete' || status === 'completed') {
    return true;
  }

  return Boolean(
    booking?.logbook_completed_at ||
      booking?.pilot_logbook_completed_at ||
      booking?.pilot_logbook_saved_at ||
      booking?.pilot_logbook_saved === true
  );
}

const CANCELLED_OR_DID_NOT_FLY_STATUSES = new Set([
  'did_not_fly',
  'not_flown',
  'cancelled',
  'canceled',
  'deleted',
]);

function isCancelledOrDidNotFlyStatus(status) {
  return CANCELLED_OR_DID_NOT_FLY_STATUSES.has(normalizePostFlightStatus(status));
}

function isCancelledOrDidNotFlyNotes(notes) {
  const text = String(notes || '').trim().toLowerCase();
  if (!text) return false;

  return text.includes('did not fly')
    || text.includes('did-not-fly')
    || text.includes('did_not_fly')
    || text.includes('not flown')
    || text.includes('cancelled')
    || text.includes('canceled');
}

// Fields saveLogbookRow() writes into pilotLogbook for a genuine flight allocation,
// plus flightTime/taxiTime/totalTime written by the booking-linked auto-logbook path in MyBookings.js.
const PILOT_LOGBOOK_HOUR_FIELDS = [
  'seDayDual',
  'seDayPic',
  'seNightDual',
  'seNightPic',
  'meDayDual',
  'meDayPic',
  'meDayCopilot',
  'meDayPiUs',
  'meNightDual',
  'meNightPic',
  'meNightCopilot',
  'meNightPiUs',
  'instrumentFlight',
  'otherFlying',
  'flightTime',
  'taxiTime',
  'totalTime',
];

function hasAnyPositivePilotLogbookHourValue(fields = {}) {
  return PILOT_LOGBOOK_HOUR_FIELDS.some((field) => {
    const value = Number(String(fields?.[field] || '').replace(',', '.'));
    return Number.isFinite(value) && value > 0;
  });
}

function hasPilotLogbookOperationalData(fields = {}) {
  return ['from', 'to', 'depUtc', 'arrUtc'].some(
    (field) => String(fields?.[field] || '').trim() !== ''
  );
}

// Single source of truth for "is this a genuine saved Pilot Logbook flight entry",
// shared by isGenuineSavedFlightEntry (flat display rows) and hasSavedPilotLogbookEntry
// (nested official-tech-log pilotLogbook data). A stray date/name/registration/note
// alone must not count; positive hours or a genuinely-saveable route/UTC entry must.
export function isGenuinePilotLogbookEntryFields(fields = {}, { techLogStatus, isDraft } = {}) {
  if (isDraft) return false;
  if (isCancelledOrDidNotFlyStatus(techLogStatus)) return false;
  if (isCancelledOrDidNotFlyNotes(fields?.notes)) return false;

  return hasAnyPositivePilotLogbookHourValue(fields) || hasPilotLogbookOperationalData(fields);
}

// For flat display rows (PilotLogbookPage.js `rows`), which carry logbook fields directly.
export function isGenuineSavedFlightEntry(row = {}) {
  return isGenuinePilotLogbookEntryFields(row, {
    techLogStatus: row?.techLogStatus,
    isDraft: row?.isDraft,
  });
}

// For a raw official_tech_logs entry, which nests logbook fields under `.pilotLogbook`.
// techLogStatus should be the owning booking's tech_log_status, when known to the caller.
export function hasSavedPilotLogbookEntry(techEntry = {}, techLogStatus) {
  const pilotLogbook = techEntry?.pilotLogbook;
  if (!pilotLogbook || typeof pilotLogbook !== 'object') return false;

  return isGenuinePilotLogbookEntryFields(pilotLogbook, { techLogStatus });
}

// A persistent mismatch: a genuine Pilot Logbook entry is saved for this
// booking, but the booking itself was never marked complete (e.g. a save that
// predates atomic save+complete, or any other out-of-band edit). Used to
// offer a durable "retry completion" recovery action rather than a passive
// warning that never resolves itself.
export function hasPersistentPilotLogbookCompletionMismatch(booking, techEntry) {
  if (!booking) return false;
  return hasSavedPilotLogbookEntry(techEntry, booking.tech_log_status) && !isLogbookCompleted(booking);
}

export function getFlightLifecycleState(booking, now = new Date()) {
  const start = parseBookingStart(booking);
  const end = parseBookingEnd(booking);

  if (!start || !end) {
    return 'unknown';
  }

  if (isPostFlightCompleted(booking) && isLogbookCompleted(booking)) {
    return 'closed';
  }

  if (isPostFlightCompleted(booking)) {
    return 'post_complete';
  }

  if (now < start) {
    return 'upcoming';
  }

  if (now < end) {
    return 'in_progress';
  }

  return 'overdue';
}

export function getActiveNextOverdueCandidates(bookings = [], now = new Date()) {
  const rows = (bookings || [])
    .map((booking) => {
      const start = parseBookingStart(booking);
      const end = parseBookingEnd(booking);
      const lifecycle = getFlightLifecycleState(booking, now);

      return {
        booking,
        start,
        end,
        lifecycle,
      };
    })
    .filter((row) => row.start && row.end && row.lifecycle !== 'closed');

  const activeRows = rows
    .filter((row) => row.lifecycle === 'in_progress')
    .sort((left, right) => left.start.getTime() - right.start.getTime());

  const futureRows = rows
    .filter((row) => row.lifecycle === 'upcoming')
    .sort((left, right) => left.start.getTime() - right.start.getTime());

  const postCompleteRows = rows
    .filter((row) => row.lifecycle === 'post_complete')
    .sort((left, right) => right.end.getTime() - left.end.getTime());

  const overdueRows = rows
    .filter((row) => row.lifecycle === 'overdue')
    .sort((left, right) => right.end.getTime() - left.end.getTime());

  return {
    activeBooking: activeRows[0]?.booking || null,
    postCompleteBooking: postCompleteRows[0]?.booking || null,
    nextFutureBooking: futureRows[0]?.booking || null,
    overdueBooking: overdueRows[0]?.booking || null,
  };
}

export function selectActiveOrNextBooking(bookings = [], now = new Date()) {
  const {
    activeBooking,
    postCompleteBooking,
    nextFutureBooking,
    overdueBooking,
  } = getActiveNextOverdueCandidates(bookings, now);

  if (activeBooking) {
    return activeBooking;
  }

  if (postCompleteBooking) {
    return postCompleteBooking;
  }

  if (nextFutureBooking) {
    return nextFutureBooking;
  }

  return overdueBooking;
}

export function selectPreFlightBooking(
  bookings = [],
  requestedBookingId = '',
  now = new Date()
) {
  const requestedId = String(
    requestedBookingId || ''
  ).trim();

  if (requestedId) {
    return (bookings || []).find(
      (booking) =>
        String(booking?.id || '') === requestedId
    ) || null;
  }

  return selectActiveOrNextBooking(
    bookings || [],
    now
  );
}

export function selectPostFlightBooking(bookings = [], requestedBookingId = '', now = new Date()) {
  const requestedId = String(requestedBookingId || '').trim();

  const rows = (bookings || [])
    .map((booking) => {
      const start = parseBookingStart(booking);
      const end = parseBookingEnd(booking);
      const lifecycle = getFlightLifecycleState(booking, now);

      return {
        booking,
        start,
        end,
        lifecycle,
      };
    })
    .filter((row) => row.start && row.end);

  if (requestedId) {
    const requested = rows.find(
      (row) => String(row.booking?.id || '') === requestedId
    );

    if (requested && requested.end <= now) {
      return requested.booking;
    }

    return null;
  }

  const eligible = rows
    .filter((row) => row.end <= now && row.lifecycle !== 'closed')
    .sort((left, right) => right.end.getTime() - left.end.getTime());

  return eligible[0]?.booking || null;
}

export async function getBookingUserIdCandidates(supabase, authUserId) {
  const candidates = new Set();
  const normalizedAuthId = String(authUserId || '').trim();

  if (!normalizedAuthId) {
    return [];
  }

  candidates.add(normalizedAuthId);

  const { data: byAuth } = await supabase
    .from('users')
    .select('id, auth_id')
    .eq('auth_id', normalizedAuthId)
    .maybeSingle();

  if (byAuth?.id) {
    candidates.add(String(byAuth.id));
  }

  if (byAuth?.auth_id) {
    candidates.add(String(byAuth.auth_id));
  }

  const { data: byId } = await supabase
    .from('users')
    .select('id, auth_id')
    .eq('id', normalizedAuthId)
    .maybeSingle();

  if (byId?.id) {
    candidates.add(String(byId.id));
  }

  if (byId?.auth_id) {
    candidates.add(String(byId.auth_id));
  }

  return [...candidates].filter(Boolean);
}
